/**
 * Date period color scale for task scheduling visualization
 *
 * Colors progress from vibrant (immediate) to desaturated (future)
 * to make near-term tasks more visually prominent
 */

export type DatePeriod = 'overdue' | 'this_week' | 'next_week' | 'week_3' | 'week_4' | 'week_5_plus' | 'unscheduled' | 'no_date';

export interface DatePeriodConfig {
  value: DatePeriod;
  label: string;
  color: string;
}

export const DATE_PERIOD_COLORS: Record<DatePeriod, string> = {
  overdue: '#EF4444',        // Red 500 - Bright red for overdue (MOST URGENT!)
  this_week: '#10B981',      // Green 500 - Full saturation (immediate)
  next_week: '#22D3EE',      // Cyan 400 - High saturation
  week_3: '#FDE68A',         // Amber 200 - Medium saturation (desaturated yellow)
  week_4: '#FED7AA',         // Orange 200 - Lower saturation (desaturated orange)
  week_5_plus: '#FECACA',    // Red 200 - Lowest saturation (desaturated red/pink)
  unscheduled: '#9CA3AF',    // Gray 400 - Neutral
  no_date: '#9CA3AF',        // Gray 400 - Alias for unscheduled
};

export const DATE_PERIOD_CONFIGS: DatePeriodConfig[] = [
  { value: 'overdue', label: 'Overdue', color: DATE_PERIOD_COLORS.overdue },
  { value: 'this_week', label: 'This Week', color: DATE_PERIOD_COLORS.this_week },
  { value: 'next_week', label: 'Next Week', color: DATE_PERIOD_COLORS.next_week },
  { value: 'week_3', label: 'Week 3', color: DATE_PERIOD_COLORS.week_3 },
  { value: 'week_4', label: 'Week 4', color: DATE_PERIOD_COLORS.week_4 },
  { value: 'week_5_plus', label: 'Week 5+', color: DATE_PERIOD_COLORS.week_5_plus },
  { value: 'unscheduled', label: 'Unscheduled', color: DATE_PERIOD_COLORS.unscheduled },
];

/**
 * Calculate which date period a given date falls into
 * Uses Sunday as end of week
 *
 * @param date - The date to check
 * @returns The period this date falls into (overdue, this_week, next_week, etc.)
 */
export function getDatePeriod(date: Date): DatePeriod {
  const now = new Date();
  now.setHours(0, 0, 0, 0);

  const itemTime = date.getTime();
  const nowTime = now.getTime();

  // Check if overdue (past due)
  if (itemTime < nowTime) {
    return 'overdue';
  }

  // Calculate end of this week (Sunday)
  const endOfThisWeek = new Date(now);
  const daysUntilSunday = 7 - now.getDay();
  endOfThisWeek.setDate(now.getDate() + daysUntilSunday);
  endOfThisWeek.setHours(23, 59, 59, 999);

  const thisWeekEnd = endOfThisWeek.getTime();

  if (itemTime <= thisWeekEnd) {
    return 'this_week';
  }

  // Calculate weeks ahead
  const weekMs = 7 * 24 * 60 * 60 * 1000;
  const weeksAhead = Math.floor((itemTime - thisWeekEnd) / weekMs) + 1;

  if (weeksAhead === 1) return 'next_week';
  if (weeksAhead === 2) return 'week_3';
  if (weeksAhead === 3) return 'week_4';
  return 'week_5_plus';
}
